/**
 * Decode bytes to text, honouring a BOM and falling back to Windows-1252.
 * `truncated` means the bytes are a prefix of a larger file, so a partial
 * multi-byte character at the end is expected and is trimmed, not mistaken
 * for a legacy encoding.
 */
export function decodeText(
  bytes: Uint8Array,
  opts: { truncated?: boolean } = {},
): { text: string; encoding: string } {
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return { text: new TextDecoder('utf-8').decode(bytes.subarray(3)), encoding: 'UTF-8' };
  }
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return { text: new TextDecoder('utf-16le').decode(bytes.subarray(2)), encoding: 'UTF-16 LE' };
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return { text: new TextDecoder('utf-16be').decode(bytes.subarray(2)), encoding: 'UTF-16 BE' };
  }
  const strict = new TextDecoder('utf-8', { fatal: true });
  try {
    return { text: strict.decode(bytes), encoding: 'UTF-8' };
  } catch {
    if (opts.truncated) {
      for (let cut = 1; cut <= 3 && cut < bytes.length; cut++) {
        try {
          return { text: strict.decode(bytes.subarray(0, bytes.length - cut)), encoding: 'UTF-8' };
        } catch {
          /* keep trimming */
        }
      }
    }
    return { text: new TextDecoder('windows-1252').decode(bytes), encoding: 'Windows-1252' };
  }
}

// ───────────────── Markdown (rendered as React nodes, never as HTML) ─────────────────

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'code'; v: string }
  | { t: 'em'; c: Inline[] }
  | { t: 'strong'; c: Inline[] }
  | { t: 'link'; href: string; c: Inline[] };

export type Block =
  | { t: 'h'; level: number; c: Inline[] }
  | { t: 'p'; c: Inline[] }
  | { t: 'ul'; items: Inline[][] }
  | { t: 'ol'; items: Inline[][] }
  | { t: 'quote'; c: Inline[] }
  | { t: 'code'; lang: string; v: string }
  | { t: 'hr' };

const SAFE_LINK = /^(https?:|mailto:)/i;

export function parseInline(src: string): Inline[] {
  const out: Inline[] = [];
  let i = 0;
  let buf = '';
  const flush = () => {
    if (buf) out.push({ t: 'text', v: buf });
    buf = '';
  };
  while (i < src.length) {
    const ch = src[i]!;
    if (ch === '`') {
      const end = src.indexOf('`', i + 1);
      if (end > i) {
        flush();
        out.push({ t: 'code', v: src.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if ((ch === '*' && src[i + 1] === '*') || (ch === '_' && src[i + 1] === '_')) {
      const mark = ch + ch;
      const end = src.indexOf(mark, i + 2);
      if (end > i + 2) {
        flush();
        out.push({ t: 'strong', c: parseInline(src.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }
    if (ch === '*' || ch === '_') {
      const end = src.indexOf(ch, i + 1);
      if (end > i + 1 && src[i + 1] !== ' ') {
        flush();
        out.push({ t: 'em', c: parseInline(src.slice(i + 1, end)) });
        i = end + 1;
        continue;
      }
    }
    if (ch === '[') {
      const close = src.indexOf('](', i + 1);
      const paren = close > 0 ? src.indexOf(')', close + 2) : -1;
      if (close > 0 && paren > close) {
        const label = src.slice(i + 1, close);
        const href = src.slice(close + 2, paren).trim();
        flush();
        if (SAFE_LINK.test(href)) out.push({ t: 'link', href, c: parseInline(label) });
        else out.push({ t: 'text', v: `${label} (${href})` });
        i = paren + 1;
        continue;
      }
    }
    buf += ch;
    i++;
  }
  flush();
  return out;
}

export function parseMarkdown(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === '') {
      i++;
      continue;
    }
    const fence = /^```(\w*)\s*$/.exec(line);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i]!)) body.push(lines[i++]!);
      i++;
      blocks.push({ t: 'code', lang: fence[1] ?? '', v: body.join('\n') });
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push({ t: 'h', level: h[1]!.length, c: parseInline(h[2]!.trim()) });
      i++;
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push({ t: 'hr' });
      i++;
      continue;
    }
    if (/^>\s?/.test(line)) {
      const q: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i]!)) q.push(lines[i++]!.replace(/^>\s?/, ''));
      blocks.push({ t: 'quote', c: parseInline(q.join(' ')) });
      continue;
    }
    if (/^\s*[-*+]\s+/.test(line)) {
      const items: Inline[][] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i]!))
        items.push(parseInline(lines[i++]!.replace(/^\s*[-*+]\s+/, '')));
      blocks.push({ t: 'ul', items });
      continue;
    }
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: Inline[][] = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i]!))
        items.push(parseInline(lines[i++]!.replace(/^\s*\d+[.)]\s+/, '')));
      blocks.push({ t: 'ol', items });
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() !== '' &&
      !/^(#{1,6}\s|```|>\s?|\s*[-*+]\s+|\s*\d+[.)]\s+)/.test(lines[i]!)
    ) {
      para.push(lines[i++]!.trim());
    }
    blocks.push({ t: 'p', c: parseInline(para.join(' ')) });
  }
  return blocks;
}

// ───────────────── CSV ─────────────────

export function detectDelimiter(sample: string): string {
  const head = sample.split('\n').slice(0, 5).join('\n');
  let best = ',';
  let bestCount = -1;
  for (const d of [',', ';', '\t', '|']) {
    const n = head.split(d).length;
    if (n > bestCount) {
      best = d;
      bestCount = n;
    }
  }
  return best;
}

export function parseCsv(
  src: string,
  maxRows = 500,
): { rows: string[][]; truncated: boolean; delimiter: string } {
  const delimiter = detectDelimiter(src);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let i = 0;
  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };
  while (i < src.length && rows.length < maxRows) {
    const c = src[i]!;
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += c;
    } else if (c === '"' && field === '') quoted = true;
    else if (c === delimiter) pushField();
    else if (c === '\n') pushRow();
    else if (c === '\r') {
      /* swallow */
    } else field += c;
    i++;
  }
  const truncated = i < src.length;
  if (!truncated && (field !== '' || row.length > 0)) pushRow();
  return { rows, truncated, delimiter };
}

// ───────────────── JSON / XML ─────────────────

export function prettyJson(text: string): { text: string; valid: boolean } {
  try {
    return { text: JSON.stringify(JSON.parse(text), null, 2), valid: true };
  } catch {
    return { text, valid: false };
  }
}

/** XML is shown as source. It is never parsed with entity expansion. */
export function xmlWarnings(text: string): string[] {
  const w: string[] = [];
  if (/<!ENTITY/i.test(text))
    w.push('Contains entity declarations. They are shown as text and never expanded.');
  if (/<!DOCTYPE[^>]*SYSTEM/i.test(text)) w.push('References an external DTD. It is never loaded.');
  return w;
}
